/**
 * Helpers for round-tripping the Biler-til-salg listing's filter/sort state
 * through the car detail page so a visitor returning via "Tilbage til oversigt"
 * lands on the same scroll/filter context they left.
 *
 * Design notes:
 * - We piggyback on a single `from` query param that holds the listing's
 *   *raw* search string (without leading `?`). This avoids duplicating the
 *   `searchSchema` here and means new filters are preserved automatically.
 * - We base64url-encode the value so it survives sharing/copy-paste even if
 *   it contains nested JSON arrays. We tolerate plain (non-encoded) values
 *   too — older detail-page visits without the encoder still work.
 * - Empty / default search states resolve to `undefined` so we never push a
 *   meaningless `?from=` onto clean URLs.
 */

const FROM_PARAM = "from";

function toBase64Url(input: string): string {
  // btoa requires a binary string. Encode to UTF-8 bytes first so non-ASCII
  // (Danish chars in `q=...`) round-trips safely.
  const utf8 = typeof TextEncoder !== "undefined"
    ? new TextEncoder().encode(input)
    : Buffer.from(input, "utf-8");
  let bin = "";
  for (let i = 0; i < utf8.length; i++) bin += String.fromCharCode(utf8[i]);
  const b64 = typeof btoa !== "undefined"
    ? btoa(bin)
    : Buffer.from(input, "utf-8").toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(input: string): string | null {
  try {
    const padded = input.replace(/-/g, "+").replace(/_/g, "/")
      + "===".slice((input.length + 3) % 4);
    if (typeof atob !== "undefined") {
      const bin = atob(padded);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return typeof TextDecoder !== "undefined"
        ? new TextDecoder().decode(bytes)
        : Buffer.from(bytes).toString("utf-8");
    }
    return Buffer.from(padded, "base64").toString("utf-8");
  } catch {
    return null;
  }
}

/**
 * Encode the current listing search string into a `from` token suitable for
 * appending to a detail-page URL. Returns `undefined` for empty/clean state
 * so callers can avoid noisy URLs.
 */
export function encodeListingFrom(searchString: string | undefined | null): string | undefined {
  if (!searchString) return undefined;
  const trimmed = searchString.replace(/^\?/, "").trim();
  if (!trimmed) return undefined;
  return toBase64Url(trimmed);
}

/**
 * Decode a `from` token back into a query string (without leading `?`).
 * Falls back to treating the input as already-plain if base64 decoding
 * doesn't produce a valid `key=value`-shaped string.
 */
export function decodeListingFrom(token: string | undefined | null): string | undefined {
  if (!token) return undefined;
  const decoded = fromBase64Url(token);
  if (decoded && /[=&]/.test(decoded)) return decoded;
  // Backwards-compat / lenient: if someone pasted a plain query string in,
  // accept it directly as long as it looks like one.
  if (/[=&]/.test(token)) return token;
  return undefined;
}

/**
 * Append `?from=<token>` to a path if the listing has any non-default state.
 * Used by `<a>`-style links where we can't pass `search` as an object.
 */
export function appendFromToHref(href: string, searchString: string | undefined | null): string {
  const token = encodeListingFrom(searchString);
  if (!token) return href;
  const sep = href.includes("?") ? "&" : "?";
  return `${href}${sep}${FROM_PARAM}=${encodeURIComponent(token)}`;
}

export const LISTING_FROM_PARAM = FROM_PARAM;
